// In CreateAccountModal.tsx
import React, { ChangeEvent, useState, useEffect, useRef } from 'react';
import SearchIcon from '../../Images/search-icon.svg';
import { ReactComponent as DropdownIcon } from '../../Images/arrow-down-4.svg';
import  apiInstance  from '../../config/HttpCommon';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {selectCreateLoading, selectCreateError, clearError } from './OnboardedCustomertableSlice';
import { CreateCustomerPayload } from '../../Services/serviceURL';
import { v4 as uuidv4 } from 'uuid';
import { usePagination } from '../../hooks/usePagination';
import Pagination from '../../Components/common/Pagination/Pagination';
import './CreateCustomer.css';

interface ModalProps {
  onClose: () => void;
  onSaveSuccess: () => void; // Add this new prop
}

interface ApiResponse<T> {
  Success: boolean;
  Message: string;
  Data: T;
  StatusCode: number;
  StackTrace: string | null;
}

interface SoldToNumber {
  id: number;
  customerId: number;
  name: string;
  siteId: number;
}

interface Customer {
  id: string;
  customerId: string;
  customerName: string;
  soldTo: string;
  cnxCustomerId: string;
  isDeleted: boolean;
  country: string;
  salesDistrict: string;
  salesRegion: string;
  state: string;
  city: string;
  // Keep these for backward compatibility if needed
  name?: string;
  description?: string;
  legacyId?: string;
  soldToNumber?: SoldToNumber[];
  soldToValues?: string[]; // Array of soldTo values for dropdown options
  subCustomers?: Customer[];
  defaultDistrict?: string;
  defaultCountry?: string;
  defaultSalesRegion?: string;
  defaultState?: string;
  defaultCity?: string;
}

interface DistrictOption {
  id: number;
  salesRegionId: number;
  salesRegion: string;
  code: string;
  name: string;
  description: string;
  active: boolean;
}



// Add these interfaces at the top with your existing interfaces
interface CountryLookupItem {
    id: number;
    type: string;
    code: string;
    value: string; // This holds the country name
    displayOrder: number;
    active: boolean;
}

interface StateLookupItem {
    stateId: number;
    stateName: string;
    countryId: number;
}

interface CityLookupItem {
    cityId: number;
    cityName: string;
    stateId: number;
}

const buildSoldToValues = (customer: Customer): string[] => {
  const soldToValues: string[] = [];

  if (customer.soldTo) {
    soldToValues.push(customer.soldTo);
  }

  for (const soldToItem of customer.soldToNumber ?? []) {
    if (soldToItem.name && !soldToValues.includes(soldToItem.name)) {
      soldToValues.push(soldToItem.name);
    }
  }

  return soldToValues;
};

const processCustomerSoldToValues = (customer: Customer): Customer => ({
  ...customer,
  soldToValues: buildSoldToValues(customer),
});

const processCustomersResponse = (responseData: Customer[]): Customer[] =>
  responseData.map(processCustomerSoldToValues);

const getPrimarySoldToLabel = (customer: Customer, soldToValue: string): string =>
  `${customer.customerName || customer.name}(#${soldToValue})`;

const getSubCustomerSoldToLabel = (customer: Customer, soldToValue: string): string =>
  `${customer.customerName || customer.name} - Sold To: ${soldToValue}`;

interface SoldToOptionItemProps {
  customer: Customer;
  soldToValue: string;
  label: string;
  isSelected: boolean;
  onSoldToSelect: (customer: Customer, soldTo: string) => void;
}

const SoldToOptionItem: React.FC<SoldToOptionItemProps> = ({
  customer,
  soldToValue,
  label,
  isSelected,
  onSoldToSelect,
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSoldToSelect(customer, soldToValue);
  };

  return (
    <div className={`sold-to-option ${isSelected ? 'selected' : ''}`} onClick={handleClick}>
      <div className="sold-to-option-content">
        <span className="sold-to-icon">📄</span>
        <span className="sold-to-label">{label}</span>
      </div>
    </div>
  );
};

interface SoldToOptionsListProps {
  customer: Customer;
  selectedCustomer: Customer | null;
  selectedSoldTo: string | null;
  getSoldToLabel: (customer: Customer, soldToValue: string) => string;
  onSoldToSelect: (customer: Customer, soldTo: string) => void;
  containerClassName?: string;
}

const SoldToOptionsList: React.FC<SoldToOptionsListProps> = ({
  customer,
  selectedCustomer,
  selectedSoldTo,
  getSoldToLabel,
  onSoldToSelect,
  containerClassName = 'customer-dropdown-options',
}) => {
  const soldToValues = customer.soldToValues ?? [];

  return (
    <div className={containerClassName}>
      {soldToValues.length > 0 ? (
        soldToValues.map((soldToValue, index) => (
          <SoldToOptionItem
            key={`${customer.id}-${index}`}
            customer={customer}
            soldToValue={soldToValue}
            label={getSoldToLabel(customer, soldToValue)}
            isSelected={
              selectedSoldTo === soldToValue && selectedCustomer?.id === customer.id
            }
            onSoldToSelect={onSoldToSelect}
          />
        ))
      ) : (
        <div className="no-sold-to-options">
          <span>No Sold To options available</span>
        </div>
      )}
    </div>
  );
};

interface SubCustomerDropdownItemProps {
  sub: Customer;
  expandedCustomerId: string | null;
  selectedCustomer: Customer | null;
  selectedSoldTo: string | null;
  onToggleExpand: (id: string) => void;
  onSoldToSelect: (customer: Customer, soldTo: string) => void;
}

const SubCustomerDropdownItem: React.FC<SubCustomerDropdownItemProps> = ({
  sub,
  expandedCustomerId,
  selectedCustomer,
  selectedSoldTo,
  onToggleExpand,
  onSoldToSelect,
}) => {
  const isExpanded = expandedCustomerId === sub.id;

  return (
    <div className="sub-customer-dropdown-container">
      <div
        className={`sub-customer-dropdown-header ${selectedCustomer?.id === sub.id ? 'selected' : ''}`}
        onClick={() => onToggleExpand(sub.id)}
      >
        <div className="customer-info">
          <span className="customer-name">{sub.customerName || sub.name}</span>
          <span className="customer-sold-to-count">
            ({sub.soldToValues?.length || 0} Sold To options)
          </span>
        </div>
        <DropdownIcon className={`dropdown-icon ${isExpanded ? 'expanded' : ''}`} />
      </div>

      {isExpanded && (
        <SoldToOptionsList
          customer={sub}
          selectedCustomer={selectedCustomer}
          selectedSoldTo={selectedSoldTo}
          getSoldToLabel={getSubCustomerSoldToLabel}
          onSoldToSelect={onSoldToSelect}
          containerClassName="sub-customer-dropdown-options"
        />
      )}
    </div>
  );
};

interface AccountCustomerListItemProps {
  customer: Customer;
  expandedCustomerId: string | null;
  selectedCustomer: Customer | null;
  selectedSoldTo: string | null;
  onToggleExpand: (id: string) => void;
  onSoldToSelect: (customer: Customer, soldTo: string) => void;
}

const AccountCustomerListItem: React.FC<AccountCustomerListItemProps> = ({
  customer,
  expandedCustomerId,
  selectedCustomer,
  selectedSoldTo,
  onToggleExpand,
  onSoldToSelect,
}) => {
  const isExpanded = expandedCustomerId === customer.id;
  const subCustomers = customer.subCustomers ?? [];

  return (
    <div className="customer-dropdown-container">
      <div
        className={`customer-dropdown-header ${selectedCustomer?.id === customer.id ? 'selected' : ''}`}
        onClick={() => onToggleExpand(customer.id)}
      >
        <div className="customer-info">
          <span className="asset-item-customer">{customer.customerName || customer.name}</span>
          <span className="customer-sold-to-count">
            ({customer.soldToValues?.length || 0} Sold To options)
          </span>
        </div>
        <DropdownIcon className={`dropdown-icon ${isExpanded ? 'expanded' : ''}`} />
      </div>

      {isExpanded && (
        <SoldToOptionsList
          customer={customer}
          selectedCustomer={selectedCustomer}
          selectedSoldTo={selectedSoldTo}
          getSoldToLabel={getPrimarySoldToLabel}
          onSoldToSelect={onSoldToSelect}
        />
      )}

      {isExpanded &&
        subCustomers.map((sub) => (
          <SubCustomerDropdownItem
            key={sub.id}
            sub={sub}
            expandedCustomerId={expandedCustomerId}
            selectedCustomer={selectedCustomer}
            selectedSoldTo={selectedSoldTo}
            onToggleExpand={onToggleExpand}
            onSoldToSelect={onSoldToSelect}
          />
        ))}
    </div>
  );
};

interface AccountCustomerListSectionProps {
  loadingCustomers: boolean;
  errorCustomers: string | null;
  paginatedCustomers: Customer[];
  expandedCustomerId: string | null;
  selectedCustomer: Customer | null;
  selectedSoldTo: string | null;
  onToggleExpand: (id: string) => void;
  onSoldToSelect: (customer: Customer, soldTo: string) => void;
}

const AccountCustomerListSection: React.FC<AccountCustomerListSectionProps> = ({
  loadingCustomers,
  errorCustomers,
  paginatedCustomers,
  expandedCustomerId,
  selectedCustomer,
  selectedSoldTo,
  onToggleExpand,
  onSoldToSelect,
}) => {
  if (loadingCustomers) {
    return <p>Loading customers...</p>;
  }

  if (errorCustomers) {
    return <p className="error-message">{errorCustomers}</p>;
  }

  if (!Array.isArray(paginatedCustomers) || paginatedCustomers.length === 0) {
    return <p>No customers found.</p>;
  }

  return (
    <>
      {paginatedCustomers.map((customer) => (
        <AccountCustomerListItem
          key={customer.id}
          customer={customer}
          expandedCustomerId={expandedCustomerId}
          selectedCustomer={selectedCustomer}
          selectedSoldTo={selectedSoldTo}
          onToggleExpand={onToggleExpand}
          onSoldToSelect={onSoldToSelect}
        />
      ))}
    </>
  );
};

const CreateAccountModal: React.FC<ModalProps> = ({ onClose, onSaveSuccess }) => { // Destructure onSaveSuccess
  const dispatch = useAppDispatch();
  const createLoading = useAppSelector(selectCreateLoading);
  const createError = useAppSelector(selectCreateError);

  const [searchText, setSearchText] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const [districts, setDistricts] = useState<DistrictOption[]>([]);

  // Add these new state variables
  const [countries, setCountries] = useState<CountryLookupItem[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [errorCountries, setErrorCountries] = useState<string | null>(null);

  const [states, setStates] = useState<StateLookupItem[]>([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [errorStates, setErrorStates] = useState<string | null>(null);

  const [cities, setCities] = useState<CityLookupItem[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [errorCities, setErrorCities] = useState<string | null>(null);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [expandedCustomerId, setExpandedCustomerId] = useState<string | null>(null);
  const [selectedSoldTo, setSelectedSoldTo] = useState<string | null>(null);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [loadingDistricts, setLoadingDistricts] = useState(true);
  const [errorCustomers, setErrorCustomers] = useState<string | null>(null);
  const [errorDistricts, setErrorDistricts] = useState<string | null>(null);


  const [accountInfo, setAccountInfo] = useState({
    customerFromConnexia: '',
    soldTo: '',
    district: '',
    country: '',
    salesRegion: '',
    state: '',
    city: '',
  });
  const [customerFromConnexia, setCustomerFromConnexia] = useState('');
  const [siteName, setSiteName] = useState('');
  const [district, setDistrict] = useState('');
  const [country, setCountry] = useState('');
  const [salesRegion, setSalesRegion] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  // Add these new state variables for Connexia search
  const [connexiaSearchText, setConnexiaSearchText] = useState('');
  const [connexiaResults, setConnexiaResults] = useState<{id: number, name: string}[]>([]);
  const [showConnexiaDropdown, setShowConnexiaDropdown] = useState(false);
  const [loadingConnexiaSearch, setLoadingConnexiaSearch] = useState(false);
  const [selectedConnexiaCustomer, setSelectedConnexiaCustomer] = useState<{id: number, name: string} | null>(null);
  const connexiaSearchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Pagination configuration
  const itemsPerPage = 10;
  const {
    currentPage,
    totalPages,
    currentData: paginatedCustomers,
    totalItems,
    goToPage,
    goToPreviousPage,
    goToNextPage,
    goToFirstPage,
    goToLastPage,
  } = usePagination({
    data: filteredCustomers,
    itemsPerPage,
  });
  
  // Clear errors when modal is opened
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);
  
  // Fetch All Customers
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoadingCustomers(true);
        setErrorCustomers(null);
        const response = await apiInstance.get<ApiResponse<Customer[]>>('/OnBoarding/GetCustomerDetails');
        const responseData = response.data.Data;

        if (!Array.isArray(responseData)) {
          console.error("API response for customers is not an array:", response.data);
          throw new Error("Unexpected API response format: Expected an array of customers under 'Data' property.");
        }

        const processedCustomers = processCustomersResponse(responseData);

        setCustomers(processedCustomers);
        setFilteredCustomers(processedCustomers); // Remove the slice(0, 50) restriction
      } catch (error: any) {
        console.error("Error fetching customers:", error);
        const errMsg = error.response?.data?.Message || error.message || 'Unknown error';
        setErrorCustomers(`Failed to load customers: ${errMsg}`);
        setCustomers([]);
        setFilteredCustomers([]);
      } finally {
        setLoadingCustomers(false);
      }
    };
    fetchCustomers();
  }, []);

  // Fetch Sales Districts
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        setLoadingDistricts(true);
        setErrorDistricts(null);
        const response = await apiInstance.get<ApiResponse<DistrictOption>>('/OnBoarding/GetSalesDistrict');
        const responseData = response.data.Data;

        if (!Array.isArray(responseData)) {
          console.error("API response for districts is not an array:", response.data);
          throw new Error("Unexpected API response format: Expected an array of districts under 'Data' property.");
        }

        setDistricts(responseData);
      } catch (error: any) {
        console.error("Error fetching districts:", error);
        const errMsg = error.response?.data?.Message || error.message || 'Unknown error';
        setErrorDistricts(`Failed to load districts: ${errMsg}`);
        setDistricts([]);
      } finally {
        setLoadingDistricts(false);
      }
    };
    fetchDistricts();
  }, []);

  // Fetch Countries
  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      setErrorCountries(null);
      setCountries([]);

      const countryDropDownType = "Country";

      try {
        const response = await apiInstance.get<ApiResponse<CountryLookupItem[]>>(`/Lookup/Country?dropDownType=${countryDropDownType}`);
       
        if (response.data.Success && Array.isArray(response.data.Data)) {
          setCountries(response.data.Data);
        } else {
          setErrorCountries(response.data.Message || 'Failed to load countries: Unexpected data format.');
        }
      } catch (error: any) {
        console.error('Error fetching countries:', error);
        const errMsg = error.response?.data?.Message || error.message || 'Unknown error';
        setErrorCountries(`Failed to load countries: ${errMsg}`);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, []);

  // Fetch States based on selected Country
  useEffect(() => {
    const fetchStates = async () => {
      setLoadingStates(true);
      setErrorStates(null);
      setStates([]);

      const selectedCountryValue = accountInfo.country;
      const selectedCountryItem = countries.find(c => c.value === selectedCountryValue);
      const selectedCountryId = selectedCountryItem?.id;

      if (!selectedCountryId) {
        setLoadingStates(false);
        setStates([]);
        setAccountInfo(prev => ({ ...prev, state: '', city: '' }));
        return;
      }

      const stateDropDownType = "State";

      try {
        const response = await apiInstance.get<ApiResponse<StateLookupItem[]>>(
          `/Lookup/State?dropDownType=${stateDropDownType}&countryId=${selectedCountryId}`
        );

        if (response.data.Success && Array.isArray(response.data.Data)) {
          setStates(response.data.Data);
        } else {
          setErrorStates(response.data.Message || 'Failed to load states: Unexpected data format.');
          setStates([]);
        }
      } catch (error: any) {
        console.error('Error fetching states:', error);
        const errMsg = error.response?.data?.Message || error.message || 'Unknown error';
        setErrorStates(`Failed to load states: ${errMsg}`);
      } finally {
        setLoadingStates(false);
      }
    };

    fetchStates();
  }, [accountInfo.country, countries]);

  // Fetch Cities based on selected State
  useEffect(() => {
    const fetchCities = async () => {
      setLoadingCities(true);
      setErrorCities(null);
      setCities([]);

      const selectedStateValue = accountInfo.state;
      const selectedStateItem = states.find(s => s.stateName === selectedStateValue);
      const selectedStateId = selectedStateItem?.stateId;

      if (!selectedStateId) {
        setLoadingCities(false);
        setCities([]);
        setAccountInfo(prev => ({ ...prev, city: '' }));
        return;
      }

      const cityDropDownType = "City";

      try {
        const response = await apiInstance.get<ApiResponse<CityLookupItem[]>>(
          `/Lookup/City?dropDownType=${cityDropDownType}&stateId=${selectedStateId}`
        );

        if (response.data.Success && Array.isArray(response.data.Data)) {
          setCities(response.data.Data);
        } else {
          setErrorCities(response.data.Message || 'Failed to load cities: Unexpected data format.');
          setCities([]);
        }
      } catch (error: any) {
        console.error('Error fetching cities:', error);
        const errMsg = error.response?.data?.Message || error.message || 'Unknown error';
        setErrorCities(`Failed to load cities: ${errMsg}`);
      } finally {
        setLoadingCities(false);
      }
    };

    fetchCities();
  }, [accountInfo.state, states]);  // --- Handlers ---
  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const text = event.target.value;
    setSearchText(text);
    const filtered = customers.filter(customer =>
      (customer.customerName || customer.name || '').toLowerCase().includes(text.toLowerCase())
    );
    setFilteredCustomers(filtered); // Remove the slice(0, 50) restriction
  };
  // Single click selects customer and populates Sold To
  // Only expand customer, do not set soldTo
  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
  };

  // Correct two-argument version
  const handleSoldToSelect = (customer: Customer, soldTo: string) => {
    setSelectedCustomer(customer);
    setSelectedSoldTo(soldTo);
    const customerName = customer.customerName || customer.name || '';
    const combinedSoldTo = `${customerName} - ${soldTo}`;
    //   ...prev,
        setConnexiaSearchText('');
        setAccountInfo(({
          customerFromConnexia: '',
          soldTo: combinedSoldTo,
          district: '',
          country: '',
          salesRegion: '',
          state: '',
          city: ''
        }));  
      };
  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setAccountInfo(prev => {
      const newState = { ...prev, [name]: value };

      // Reset dependent dropdowns if a higher-level selection changes
      if (name === "country" && prev.country !== value) {
        newState.state = '';
        newState.city = '';
      }
      if (name === "state" && prev.state !== value) {
        newState.city = '';
      }      // Auto-populate sales region when district is selected
      if (name === "district" && value) {
        const selectedDistrict = districts.find(district => district.id.toString() === value);
        if (selectedDistrict && selectedDistrict.salesRegion) {
          newState.salesRegion = selectedDistrict.salesRegion;
        }
      }
      
      return newState;
    });
  };

  // Move generate24DigitHex function here
  const generate24DigitHex = () => {
    return Array.from({length: 24}, () => Math.floor(Math.random() * 16).toString(16)).join('');
  };

  // Function to show toast notification
  const showToastNotification = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    
    // Auto-hide toast after 5 seconds
    setTimeout(() => {
      setShowToast(false);
    }, 5000);
  };

  // Cleanup function to clear errors when modal closes
  const handleClose = () => {
    dispatch(clearError());
    onClose();
  };

  const handleSave = async () => {
    // Clear any previous errors
    dispatch(clearError());
    
    // Validation
    if (!selectedCustomer) {
      showToastNotification("Please select a customer.", 'error');
      return;
    }
    if (!selectedConnexiaCustomer || !connexiaSearchText.trim()) {
      showToastNotification("Please search and select a customer from Connexia.", 'error');
      return;
    }
    if (!accountInfo.soldTo.trim()) {
      showToastNotification("Please enter a Sold To Number.", 'error');
      return;
    }
    if (!accountInfo.district) {
      showToastNotification("Please select a district.", 'error');
      return;
    }
    if (!accountInfo.country) {
      showToastNotification("Please select a country.", 'error');
      return;
    }    try {
      // Generate a fresh 24-digit hex string for each save
      const cnxCustomerId = generate24DigitHex();
        const customerPayload: CreateCustomerPayload = {
        id: "", // Send empty string instead of UUID
        createdBy: 'current_user',
        modifiedBy: 'current_user',
        createdOn: new Date().toISOString(),
        modifiedOn: new Date().toISOString(),
        isDeleted: false,
        customerId: uuidv4(),
        customerName: selectedConnexiaCustomer.name, // Use Connexia customer name
        soldTo: accountInfo.soldTo,
        CnxCustomerId: selectedConnexiaCustomer.id.toString(), // Use Connexia customer ID
        country: accountInfo.country,
        salesDistrict: accountInfo.district,
        salesRegion: accountInfo.salesRegion,
        state: accountInfo.state,
        city: accountInfo.city
      };      

      // Show success toast
      showToastNotification('Customer account created successfully!', 'success');
      
      // Close modal after a short delay
      setTimeout(() => {
        dispatch(clearError()); // Clear any error state
        onSaveSuccess();
        onClose();
      }, 1500);

    } catch (error: any) {
      console.error("Error creating customer account:", error);
      
      // Default error message
      let errorMessage = 'An error occurred while creating the customer account. Please try again.';
      
      // Check various error structures to find the API message
      if (typeof error === 'string') {
        // If error is a string, use it directly
        errorMessage = error;
      } else if (error && error.message) {
        // If error has a message property
        errorMessage = error.message;
      } else if (error && error.response && error.response.data && error.response.data.Message) {
        // If error has the API response structure
        errorMessage = error.response.data.Message;
      }
      
      // Show the toast with the extracted error message
      showToastNotification(errorMessage, 'error');
      
      // Auto-close the modal after 5 seconds when error occurs
      setTimeout(() => {
        dispatch(clearError()); // Clear any error state
        onClose();
      }, 3000);
    }
  };
  const toggleExpand = (id: string) => {
    setExpandedCustomerId(prev => (prev === id ? null : id));
  };
  // Add this function after your existing handlers
  const searchConnexiaCustomers = async (searchTerm: string) => {
    if (searchTerm.length < 3) {
      setConnexiaResults([]);
      setShowConnexiaDropdown(false);
      return;
    }

    try {
      setLoadingConnexiaSearch(true);
      const response = await apiInstance.get<ApiResponse<{id: number, name: string}[]>>(`/Connexia/search/${encodeURIComponent(searchTerm)}`);
      
      if (response.data.Success && Array.isArray(response.data.Data)) {
        setConnexiaResults(response.data.Data);
        setShowConnexiaDropdown(true);
      } else {
        console.error('Unexpected API response format:', response.data);
        setConnexiaResults([]);
        setShowConnexiaDropdown(false);
      }
    } catch (error: any) {
      console.error('Error searching Connexia customers:', error);
      setConnexiaResults([]);
      setShowConnexiaDropdown(false);
    } finally {
      setLoadingConnexiaSearch(false);
    }
  };

  const scheduleConnexiaSearch = (searchTerm: string) => {
    if (connexiaSearchTimeoutRef.current) {
      clearTimeout(connexiaSearchTimeoutRef.current);
    }
    connexiaSearchTimeoutRef.current = setTimeout(searchConnexiaCustomers, 300, searchTerm);
  };

  // Add these handlers after your existing handlers

  const handleConnexiaSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConnexiaSearchText(value);
    
    // Update the accountInfo state
    setAccountInfo(prev => ({
      ...prev,
      customerFromConnexia: value
    }));

    if (value.length < 3) {
      setConnexiaResults([]);
      setShowConnexiaDropdown(false);
      setSelectedConnexiaCustomer(null);
      return;
    }

    // Call the debounced search function
    scheduleConnexiaSearch(value);
  };

  const handleConnexiaCustomerSelect = (customer: {id: number, name: string}) => {
    setSelectedConnexiaCustomer(customer);
    setConnexiaSearchText(customer.name);
    setAccountInfo(prev => ({
      ...prev,
      customerFromConnexia: customer.name
    }));
    setShowConnexiaDropdown(false);
    setConnexiaResults([]);
  };

  const handleConnexiaSearchFocus = () => {
    if (connexiaSearchText.length >= 3 && connexiaResults.length > 0) {
      setShowConnexiaDropdown(true);
    }
  };

  const handleConnexiaSearchBlur = () => {
    // Delay hiding to allow clicking on dropdown items
    setTimeout(() => {
      setShowConnexiaDropdown(false);
    }, 200);
  };
  

  // --- Render ---
  return (
    <div className="modal-overlay">
      <div className="modal-content-customer">
        <h2 className='create-title'>Create Account</h2>
        <hr className="modal-header-divider" />
        <div className="modal-sections">
          <div className="left-section">
            <div className="card-container">
              <div className="search-container-cust">
                <img className='search-icon-cust' src={SearchIcon} alt='Search' />
                <input
                  type="text"
                  placeholder="Search"
                  className="search-input-account"
                  value={searchText}
                  onChange={handleSearchChange}
                />
              </div>
            </div>
            <div className="customer-list">
              <AccountCustomerListSection
                loadingCustomers={loadingCustomers}
                errorCustomers={errorCustomers}
                paginatedCustomers={paginatedCustomers}
                expandedCustomerId={expandedCustomerId}
                selectedCustomer={selectedCustomer}
                selectedSoldTo={selectedSoldTo}
                onToggleExpand={toggleExpand}
                onSoldToSelect={handleSoldToSelect}
              />
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={goToPage}
              onPreviousPage={goToPreviousPage}
              onNextPage={goToNextPage}
            />
          </div>

          <div className="vertical-divider"></div>

          <div className="right-section">
            <div className="card-container">
              <h3>Enter site information</h3>
                <div className="form-grid">   
                  <div className="form-group-create">
                  <label className='required'>Sold To (Location)</label>
                  <input
                    className="btn-clr-create"
                    type="text"
                    name="soldTo"
                    value={accountInfo.soldTo }
                    onChange={handleInputChange}
                    placeholder="Enter Sold To number"
                    disabled={loadingCustomers}
                    readOnly
                  />
                </div>           
                <div className="form-group-create">
                <label className="required">Parent Company</label>
                <div className="connexia-searchable-dropdown-container">
                  <input
                    className="btn-clr-create connexia-searchable-input"
                    type="text"
                    name="customerFromConnexia"
                    value={connexiaSearchText}
                    onChange={handleConnexiaSearchChange}
                    onFocus={handleConnexiaSearchFocus}
                    onBlur={handleConnexiaSearchBlur}
                    placeholder="Type at least 3 characters to search..."
                    autoComplete="off"
                    disabled={loadingCustomers}
                  />
                  
                  {loadingConnexiaSearch && (
                    <div className="connexia-loading-indicator">
                      <span>Searching...</span>
                    </div>
                  )}
                  
                  {showConnexiaDropdown && connexiaResults.length > 0 && (
                    <div className="connexia-dropdown-list">
                      {connexiaResults.map(customer => (
                        <div
                          key={customer.id}
                          className="connexia-dropdown-item"
                          onMouseDown={() => handleConnexiaCustomerSelect(customer)}
                        >
                          <div className="connexia-customer-info">
                            <span className="connexia-customer-name">{customer.name}</span>
                            <span className="connexia-customer-id">ID: {customer.id}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {showConnexiaDropdown && connexiaResults.length === 0 && connexiaSearchText.length >= 3 && !loadingConnexiaSearch && (
                    <div className="connexia-dropdown-list">
                      <div className="connexia-dropdown-item no-results">
                        No customers found for "{connexiaSearchText}"
                      </div>
                    </div>
                  )}
                  
                  {connexiaSearchText.length > 0 && connexiaSearchText.length < 3 && (
                    <div className="connexia-search-hint">
                      Type at least 3 characters to search
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group-create">
                <label className='required'>District</label>
                <select
                  className="btn-bg-clr-create"
                  name="district"
                  value={accountInfo.district}
                  onChange={handleInputChange}
                  disabled={loadingDistricts || loadingCustomers}
                >
                  <option value="">{loadingDistricts ? 'Loading districts...' : 'Select a District'}</option>
                  {districts.map(option => (
                    <option key={option.id} value={option.id}>{option.name}</option>
                  ))}
                </select>
                {errorDistricts && <p className="error-message-inline">{errorDistricts}</p>}
              </div>

              
              <div className="form-group-create">
                <label>Sales region</label>
                <input
                  className="btn-clr-create"
                  type="text"
                  name="salesRegion"
                  value={accountInfo.salesRegion}
                  onChange={handleInputChange}
                  placeholder="(auto selected on district)"
                  disabled={loadingCustomers}
                />
              </div>

              <div className="form-group-create">
                <label className='required'>Country</label>
                <select 
                  className="btn-bg-clr-create" 
                  name="country" 
                  value={accountInfo.country} 
                  onChange={handleInputChange}
                  disabled={loadingCountries || loadingCustomers}
                >
                  <option value="">{loadingCountries ? 'Loading countries...' : 'Select a Country'}</option>
                  {errorCountries && <option disabled>{errorCountries}</option>}
                  {!loadingCountries && !errorCountries && countries.length > 0 && (
                    countries.map((country) => (
                      <option key={country.id} value={country.value}>{country.value}</option>
                    ))
                  )}
                </select>
                {errorCountries && <p className="error-message-inline">{errorCountries}</p>}
              </div>

              <div className="form-group-create">
                <label>State</label>
                <select 
                  className="btn-bg-clr-create" 
                  name="state" 
                  value={accountInfo.state} 
                  onChange={handleInputChange}
                  disabled={loadingStates || !accountInfo.country || loadingCustomers}
                >
                  <option value="">{loadingStates ? 'Loading states...' : 'Select a State'}</option>
                  {errorStates && <option disabled>{errorStates}</option>}
                  {!loadingStates && !errorStates && states.length > 0 && (
                    states.map((state) => (
                      <option key={state.stateId} value={state.stateName}>{state.stateName}</option>
                    ))
                  )}
                  {!loadingStates && !errorStates && states.length === 0 && accountInfo.country && (
                    <option disabled>No states available for this country</option>
                  )}
                </select>
                {errorStates && <p className="error-message-inline">{errorStates}</p>}
              </div>

              <div className="form-group-create">
                <label>City</label>
                <select 
                  className="btn-bg-clr-create" 
                  name="city" 
                  value={accountInfo.city} 
                  onChange={handleInputChange}
                  disabled={loadingCities || !accountInfo.state || loadingCustomers}
                >
                  <option value="">{loadingCities ? 'Loading cities...' : 'Select a City'}</option>
                  {errorCities && <option disabled>{errorCities}</option>}
                  {!loadingCities && !errorCities && cities.length > 0 && (
                    cities.map((city) => (
                      <option key={city.cityId} value={city.cityName}>{city.cityName}</option>
                    ))
                  )}
                  {!loadingCities && !errorCities && cities.length === 0 && accountInfo.state && (
                    <option disabled>No cities available for this state</option>
                  )}
                </select>
                {errorCities && <p className="error-message-inline">{errorCities}</p>}
              </div>

                </div>
            </div>
          </div>
        </div>        <div className="modal-actions">
          <button className="Cancel-btn" onClick={handleClose}>Cancel</button>
          <button 
            className="Save-btn" 
            onClick={handleSave} 
            disabled={createLoading || loadingDistricts || loadingCountries || loadingCustomers}
          >
            {createLoading ? 'Creating...' : 'Save'}
          </button>
        </div>

        {/* Toast Notification */}
        {showToast && (
          <div className={`toast ${toastType}`}>
            <span>{toastMessage}</span>
            <button 
              className="toast-close" 
              onClick={() => setShowToast(false)}
            >
              ×
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateAccountModal;